/**
 * Morale — the Brief's signature interlocking pressure.
 *
 * Range: 0–100. Bands map to states with combat effects:
 *   80-100 Inspired   +5% damage
 *   50-79  Steady     normal
 *   25-49  Shaken     hesitates 15% of attack ticks, -10% damage
 *   10-24  Panicked   hesitates 35% of ticks, -25% damage
 *    0-9   Routed     enemy: drops attacks, flees to map edge
 *                     party: cannot attack, slowed
 */
export type MoraleState = 'inspired' | 'steady' | 'shaken' | 'panicked' | 'routed';

export const MORALE_BANDS: Array<{
  min: number; max: number; state: MoraleState;
  damageMod: number; hesitateChance: number; tint: number; label: string;
}> = [
  { min: 80, max: 100, state: 'inspired', damageMod: 1.05, hesitateChance: 0.0,  tint: 0x9ef58e, label: 'INSPIRED' },
  { min: 50, max: 79,  state: 'steady',   damageMod: 1.0,  hesitateChance: 0.0,  tint: 0xffd54a, label: 'STEADY' },
  { min: 25, max: 49,  state: 'shaken',   damageMod: 0.9,  hesitateChance: 0.15, tint: 0xff9b3a, label: 'SHAKEN' },
  { min: 10, max: 24,  state: 'panicked', damageMod: 0.75, hesitateChance: 0.35, tint: 0xff5a4a, label: 'PANICKED' },
  { min: 0,  max: 9,   state: 'routed',   damageMod: 0.5,  hesitateChance: 0.6,  tint: 0xa12d2d, label: 'ROUTED' },
];

export function bandFor(value: number) {
  const v = Math.max(0, Math.min(100, value));
  for (const b of MORALE_BANDS) if (v >= b.min && v <= b.max) return b;
  return MORALE_BANDS[1]; // steady fallback
}

// Per-faction morale modifiers. Pulled from UnitDef.role (and explicit enemy type
// in M5 spawning) to apply Brief-flavored faction texture.
export interface MoraleProfile {
  base: number;             // starting morale
  resolve: number;          // 0..1 multiplier on incoming morale damage (lower = tougher)
  panicSpreadRadius: number; // tiles
  immunities: Set<'fear' | 'rout'>; // e.g. undead ignore fear
}

export const DEFAULT_PROFILE: MoraleProfile = {
  base: 65,
  resolve: 1.0,
  panicSpreadRadius: 4,
  immunities: new Set(),
};

export const PROFILES: Record<string, MoraleProfile> = {
  // Party
  vanguard: { base: 80, resolve: 0.6, panicSpreadRadius: 3, immunities: new Set() },
  marksman: { base: 70, resolve: 0.8, panicSpreadRadius: 3, immunities: new Set() },
  medic:    { base: 70, resolve: 0.7, panicSpreadRadius: 3, immunities: new Set() },
  mage:     { base: 75, resolve: 0.55, panicSpreadRadius: 3, immunities: new Set() }, // disciplined scholar
  // Enemies (by spawn key)
  goblin:          { base: 55, resolve: 1.2, panicSpreadRadius: 4, immunities: new Set() },
  skeleton_archer: { base: 100, resolve: 0.0, panicSpreadRadius: 0, immunities: new Set(['fear', 'rout']) },
  orc_brute:       { base: 75, resolve: 0.7, panicSpreadRadius: 3, immunities: new Set() },
};
